import { AlertTriangle, Ban } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { cn } from '../../../lib/utils';
import type { KeybindingNotice } from '../model/keybindingTypes';

export type KeybindingNoticeRowProps = {
  readonly notice: KeybindingNotice;
  readonly text: string;
  /** Only when the store holds an undo snapshot — never after hydration or an import. */
  readonly canUndo: boolean;
  readonly onUndo: () => void;
};

/**
 * One durable change validation made (plan D6): a chord that moved to another
 * command, or a reserved chord that was refused. `keybinding-notice-<id>` is
 * keyed by the command that lost the chord.
 */
export function KeybindingNoticeRow({ notice, text, canUndo, onUndo }: KeybindingNoticeRowProps) {
  const isError = notice.severity === 'error';
  const Icon = isError ? Ban : AlertTriangle;
  return (
    <div
      role="status"
      className={cn(
        'flex items-start gap-2 rounded-md border px-3 py-2 text-xs',
        isError
          ? 'border-destructive/40 bg-destructive/10 text-foreground'
          : 'border-amber-500/40 bg-amber-500/10 text-foreground',
      )}
      data-testid={`keybinding-notice-${notice.commandId}`}
    >
      <Icon className={cn('mt-0.5 h-3.5 w-3.5 shrink-0', isError ? 'text-destructive' : 'text-warning')} />
      <span className="flex-1">{text}</span>
      {canUndo && (
        <Button
          variant="ghost"
          size="xs"
          className="-my-1 h-6 px-2 text-xs"
          data-testid={`keybinding-undo-${notice.commandId}`}
          onClick={onUndo}
        >
          Undo
        </Button>
      )}
    </div>
  );
}
